import { layoutTokens, typographyTokens } from "@/shared/theme";
import { Box, Tab, Tabs } from "@mui/material";
import { useEffect, useRef, type ReactNode, type SyntheticEvent } from "react";
import type { GameReviewTab } from "../review-navigation";

interface GameReviewTabsProps {
  activeTab: GameReviewTab;
  onChange: (tab: GameReviewTab) => void;
  review: ReactNode;
  nikola: ReactNode;
  moves: ReactNode;
  details: ReactNode;
  feedback?: ReactNode;
  reviewLabel?: string;
  idPrefix?: string;
  unavailable?: Partial<Record<GameReviewTab, string>>;
}

const tabs: readonly { id: GameReviewTab; label: string }[] = [
  { id: "review", label: "01 REVIEW" },
  { id: "nikola", label: "02 NIKOLA" },
  { id: "moves", label: "03 MOVES" },
  { id: "details", label: "04 DETAILS" },
];

export const GameReviewTabs = ({
  activeTab,
  onChange,
  review,
  nikola,
  moves,
  details,
  feedback,
  reviewLabel = "REVIEW",
  idPrefix = "game-review",
  unavailable = {},
}: GameReviewTabsProps) => {
  const panels: Record<GameReviewTab, ReactNode> = { review, nikola, moves, details };
  const reason = (id: GameReviewTab) =>
    id === "nikola" && import.meta.env.VITE_NIKOLA_DISABLED === "true"
      ? "Nikola is coming soon."
      : unavailable[id] ?? (panels[id] == null || panels[id] === false ? "No content available yet." : undefined);
  const selectedTab = reason(activeTab) ? (tabs.find(({ id }) => !reason(id))?.id ?? "review") : activeTab;
  const handleChange = (_event: SyntheticEvent, value: GameReviewTab) => {
    if (!reason(value)) onChange(value);
  };
  useEffect(() => {
    if (selectedTab !== activeTab) onChange(selectedTab);
  }, [activeTab, selectedTab, onChange]);
  const activePanel = useRef<HTMLDivElement>(null);
  const feedbackActive = Boolean(feedback);
  useEffect(() => {
    if (feedbackActive && activePanel.current) activePanel.current.scrollTop = 0;
  }, [activeTab, feedbackActive]);

  return (
    <Box
      component="section"
      aria-label="Game review workspace"
      data-tour-layer={`review:${selectedTab}`}
      sx={{ minWidth: 0, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Tabs
        data-tour="review-tabs"
        value={selectedTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons={false}
        aria-label="Game review sections"
        sx={{
          minHeight: layoutTokens.controlHeight,
          flexShrink: 0,
          borderBlock: `${layoutTokens.structuralBorder}px solid`,
          borderColor: "divider",
          bgcolor: "background.paper",
          "& .MuiTabs-flexContainer": { minWidth: layoutTokens.reviewTabMinWidth * 4 },
          "& .MuiTabs-indicator": { display: "none" },
          "& .MuiTab-root": {
            flex: `1 0 ${layoutTokens.reviewTabMinWidth}px`,
            minWidth: layoutTokens.reviewTabMinWidth,
            minHeight: layoutTokens.controlHeight,
            px: { xs: 0.5, sm: 1 },
            whiteSpace: "nowrap",
            borderInlineEnd: `${layoutTokens.dividerBorder}px solid`,
            borderColor: "divider",
            color: "text.secondary",
            fontSize: { xs: "caption.fontSize", sm: "caption.fontSize" },
            fontWeight: "fontWeightBold",
            letterSpacing: typographyTokens.tracking.label,
            "&:hover": { bgcolor: "secondary.main", color: "secondary.contrastText" },
            "&.Mui-selected": {
              bgcolor: "primary.main",
              color: "primary.contrastText",
              boxShadow: (theme) => `inset 0 -${layoutTokens.accentBorder}px 0 ${theme.palette.secondary.main}`,
            },
            "&.Mui-focusVisible": {
              outline: (theme) => `${layoutTokens.structuralBorder}px solid ${theme.palette.secondary.main}`,
              outlineOffset: -3,
            },
          },
        }}
      >
        {tabs.map(({ id, label }) => (
          <Tab
            key={id}
            id={`${idPrefix}-tab-${id}`}
            aria-controls={`${idPrefix}-panel-${id}`}
            value={id}
            disabled={Boolean(reason(id))}
            title={reason(id)}
            aria-label={reason(id) ? `${id === "review" ? reviewLabel : label} — ${reason(id)}` : undefined}
            label={id === "review" ? `01 ${reviewLabel}` : label}
            onClick={(event) => {
              if (event.detail > 0) requestAnimationFrame(() => activePanel.current?.focus({ preventScroll: true }));
            }}
          />
        ))}
      </Tabs>
      {tabs.map(({ id }) => (
        <Box
          key={id}
          ref={selectedTab === id ? activePanel : undefined}
          id={`${idPrefix}-panel-${id}`}
          role="tabpanel"
          tabIndex={-1}
          aria-labelledby={`${idPrefix}-tab-${id}`}
          data-tour={`review-${id}-panel`}
          hidden={selectedTab !== id}
          data-lenis-prevent
          sx={{
            minWidth: 0,
            minHeight: 0,
            flex: 1,
            overflowY: "auto",
            overscrollBehaviorY: "contain",
            pt: 1,
            pr: { lg: 0.5 },
          }}
        >
          {selectedTab === id && feedback}
          {!reason(id) && panels[id]}
        </Box>
      ))}
    </Box>
  );
};

export const GameReviewWorkspace = ({
  board,
  navigation,
  ...tabs
}: GameReviewTabsProps & { board: ReactNode; navigation: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      minWidth: 0,
      minHeight: 0,
      height: { xs: "auto", sm: "100%" },
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        md: "minmax(0, 3fr) minmax(0, 2fr)",
      },
      gridTemplateRows: { xs: "auto auto auto", sm: "auto minmax(0, 3fr) minmax(0, 2fr)", md: "auto minmax(0, 1fr)" },
      gridTemplateAreas: { xs: '"navigation" "board" "workspace"', md: '"board navigation" "board workspace"' },
      columnGap: layoutTokens.contentGap,
      rowGap: layoutTokens.controlGap,
      alignItems: "stretch",
    }}
  >
    <Box data-testid="game-review-navigation" sx={{ gridArea: "navigation", minWidth: 0 }}>
      {navigation}
    </Box>
    <Box
      data-testid="game-review-board-rail"
      data-lenis-prevent
      sx={{
        gridArea: "board",
        minWidth: 0,
        minHeight: 0,
        height: { xs: "auto", sm: "100%" },
        overflowY: { xs: "visible", sm: "auto" },
        overflowX: "hidden",
        overscrollBehavior: "contain",
      }}
    >
      {board}
    </Box>
    <Box sx={{ gridArea: "workspace", minWidth: 0, minHeight: 0, height: "100%" }}>
      <GameReviewTabs {...tabs} />
    </Box>
  </Box>
);
